import React, { useRef, useState, useEffect } from 'react';
import { IcCamera, IcImage, IcClose } from '../theme/icons';

// QR reader with NO external dependency: uses the browser-native BarcodeDetector
// (Chrome). Supports both live camera scan and reading from an uploaded image.
// Falls back gracefully (the parent always keeps a manual text field).
const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

const QrScanner = ({ onDetected }) => {
  const [camOpen, setCamOpen] = useState(false);
  const [err, setErr] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const fileRef = useRef(null);

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOpen(false);
  };

  // Clean up on unmount.
  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    if (!supported) {
      setErr("Scan caméra non supporté par ce navigateur — utilisez Chrome, lisez une image, ou saisissez le code.");
      return;
    }
    setErr(null);
    setCamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const tick = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            onDetected(codes[0].rawValue);
            stopCamera();
            return;
          }
        } catch (_) { /* keep scanning */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (_) {
      setErr("Impossible d'accéder à la caméra.");
      setCamOpen(false);
    }
  };

  const readImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr(null);
    if (!supported) {
      setErr("Lecture QR non supportée par ce navigateur — utilisez Chrome ou saisissez le code.");
      return;
    }
    try {
      const bitmap = await createImageBitmap(file);
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const codes = await detector.detect(bitmap);
      if (codes && codes.length) onDetected(codes[0].rawValue);
      else setErr("Aucun QR code trouvé dans l'image.");
    } catch (_) {
      setErr("Erreur lors de la lecture de l'image.");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={startCamera}
          className="btn-s px-3 py-2 text-sm"
        >
          <IcCamera /> Scanner (caméra)
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="btn-s px-3 py-2 text-sm"
        >
          <IcImage /> Lire depuis une image
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={readImage} />
      </div>

      {err && <p className="text-xs text-amber-600 mt-2">{err}</p>}

      {camOpen && (
        <div className="modal-backdrop z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl overflow-hidden w-full max-w-md shadow-2xl">
            <div className="modal-head">
              <span className="modal-title flex items-center gap-2"><IcCamera /> Scanner un QR code</span>
              <button onClick={stopCamera} className="text-gray-400 hover:text-white"><IcClose /></button>
            </div>
            <video ref={videoRef} className="w-full bg-black aspect-square object-cover" playsInline muted />
            <p className="text-center text-sm text-gray-500 py-3">Pointez la caméra vers le QR code…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrScanner;
